import { Text, useTick } from '@pixi/react';
import { useMemo, useRef } from 'react';
import { Text as PixiText, TextStyle } from 'pixi.js';
import { useGameStore } from '../../store/gameSlice';
import { Element } from '../../types/enums';
import { ELEMENT_COLORS } from '../../data/elements.config';

type EffectDisplayType = 'damage' | 'shieldDamage' | 'healHp' | 'healShield';

interface EffectTextDisplayProps {
  amount: number;
  type: EffectDisplayType;
  worldX: number;
  worldY: number;
  id: string;
  crit?: boolean;
  element?: Element;
  onComplete: (id: string) => void;
}

const STYLE: Record<EffectDisplayType, { fill: string; prefix: string }> = {
  damage: { fill: '#ff4444', prefix: '-' },
  shieldDamage: { fill: '#aaaaaa', prefix: '-' },
  healHp: { fill: '#44ff44', prefix: '+' },
  healShield: { fill: '#88ccff', prefix: '+' },
};

const DURATION = 100;
const RISE_PX = 30;

/**
 * The animation (y + alpha) is applied directly on the PIXI Text instance via
 * a ref, never through React setState, so the component does not re-render
 * during its lifetime. `onComplete(id)` fires once at the end so the parent
 * can remove the entry from its list.
 */
export const EffectTextDisplay = ({
  amount,
  type,
  worldX,
  worldY,
  id,
  crit = false,
  element,
  onComplete,
}: EffectTextDisplayProps) => {
  // One-shot read of the zoom at creation: over the float's lifetime (~1.6 s) a
  // zoom change is rare and the component disappears before it would be noticed.
  // Avoids a subscription that would re-render on every zoom change.
  const zoomFactor = useRef(useGameStore.getState().zoomFactor).current;

  const textRef = useRef<PixiText | null>(null);
  const elapsedRef = useRef(0);
  const completedRef = useRef(false);

  const { fill: typeFill, prefix } = STYLE[type];
  const fill = element ? ELEMENT_COLORS[element] : typeFill;

  // Stable reference so PIXI does not have to reprocess the style each frame.
  const style = useMemo(
    () =>
      ({
        fontSize: 10 * zoomFactor,
        fill,
        fontWeight: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      }) as TextStyle,
    [zoomFactor, fill],
  );

  const baseY = -worldY * zoomFactor;

  useTick((delta) => {
    if (completedRef.current) {
      return;
    }
    elapsedRef.current += delta;
    const progress = Math.min(elapsedRef.current / DURATION, 1);

    const t = textRef.current;
    if (t) {
      t.y = baseY - progress * RISE_PX;
      t.alpha = 1 - progress;
    }

    if (elapsedRef.current >= DURATION) {
      completedRef.current = true;
      onComplete(id);
    }
  });

  return (
    <Text
      ref={textRef}
      text={`${prefix}${amount}${crit ? '!' : ''}`}
      anchor={0.5}
      x={worldX * zoomFactor}
      y={baseY}
      alpha={1}
      zIndex={100}
      style={style}
    />
  );
};
